import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction
} from 'react';
import {
  AddOn,
  Appointment,
  BookingAvailabilitySlot,
  Company,
  MedicalCertificate,
  Service,
  SystemConfig,
  Venue
} from '@/types';
import { api } from '@/services/api';
import { normalizeDateOnly } from '@/lib/dates';
import { filterPastAvailabilitySlots, getAvailableTimeSlotsForDate } from '@/lib/scheduling';
import { normalizeErrorMessage } from '@/lib/errorUtils';
import {
  buildCertificateFileName,
  buildCertificateHtml,
  openCertificatePrintWindow,
  resolveCertificateClientName
} from '@/lib/certificateRenderer';
import { showAlertModal, showConfirmModal } from '@shared/components/common/AlertModal';

interface DashboardParams {
  company: Company;
  servicesById: Map<string, Service>;
  venuesById: Map<string, Venue>;
  addOnsById: Map<string, AddOn>;
  certificatesById: Map<string, MedicalCertificate>;
  systemConfig?: SystemConfig;
  onForceRefresh?: () => Promise<void>;
}

interface UseCompanyDashboardBookingsState {
  editingBooking: Appointment | null;
  setEditingBooking: Dispatch<SetStateAction<Appointment | null>>;
  isLoadingEditingAvailability: boolean;
  editingTimeOptions: string[];
  bookingActionError: string;
  isSavingBooking: boolean;
  openEditBooking: (app: Appointment) => void;
  closeEditBooking: () => void;
  handleSaveBooking: () => Promise<void>;
  handleCancelBooking: () => Promise<void>;
  handleViewCertificate: (app: Appointment) => void;
}

export const useCompanyDashboardBookings = ({
  company,
  servicesById,
  venuesById,
  addOnsById,
  certificatesById,
  systemConfig,
  onForceRefresh
}: DashboardParams): UseCompanyDashboardBookingsState => {
  const [editingBooking, setEditingBooking] = useState<Appointment | null>(null);
  const [editingBookingOriginal, setEditingBookingOriginal] = useState<Appointment | null>(null);
  const [bookingActionError, setBookingActionError] = useState('');
  const [isSavingBooking, setIsSavingBooking] = useState(false);
  const [editingAvailabilitySlots, setEditingAvailabilitySlots] = useState<
    BookingAvailabilitySlot[]
  >([]);
  const [hasEditingAvailabilityData, setHasEditingAvailabilityData] = useState(false);
  const [isLoadingEditingAvailability, setIsLoadingEditingAvailability] = useState(false);

  const refreshCompanyData = useCallback(async () => {
    if (!onForceRefresh) return;
    await onForceRefresh();
  }, [onForceRefresh]);

  const getBookingDurationMinutes = useCallback(
    (booking: Appointment) => {
      const service = servicesById.get(booking.serviceId);
      let duration = service?.durationMinutes || 30;

      for (const addOnId of booking.addOnIds || []) {
        const addOn = addOnsById.get(addOnId);
        if (addOn) {
          duration += addOn.durationMinutes || 0;
        }
      }

      return duration;
    },
    [addOnsById, servicesById]
  );

  const openEditBooking = useCallback((app: Appointment) => {
    const snapshot = { ...app, addOnIds: [...(app.addOnIds || [])] };
    setEditingBooking(snapshot);
    setEditingBookingOriginal(snapshot);
    setBookingActionError('');
  }, []);

  const closeEditBooking = useCallback(() => {
    setEditingBooking(null);
    setEditingBookingOriginal(null);
    setBookingActionError('');
    setEditingAvailabilitySlots([]);
    setHasEditingAvailabilityData(false);
    setIsLoadingEditingAvailability(false);
  }, []);

  const handleSaveBooking = useCallback(async () => {
    if (!editingBooking) return;
    setIsSavingBooking(true);
    setBookingActionError('');
    try {
      await api.updateBooking(editingBooking);
      closeEditBooking();
      await refreshCompanyData();
      showAlertModal('Appointment updated.');
    } catch (e: unknown) {
      setBookingActionError(normalizeErrorMessage(e, 'Failed to update appointment.'));
    } finally {
      setIsSavingBooking(false);
    }
  }, [closeEditBooking, editingBooking, refreshCompanyData]);

  const handleCancelBooking = useCallback(async () => {
    if (!editingBooking) return;
    const confirmed = await showConfirmModal(
      `Cancel appointment #${editingBooking.id}? This will mark the booking as cancelled.`,
      {
        title: 'Cancel Appointment',
        confirmText: 'Cancel Appointment',
        cancelText: 'Keep Booking',
        isDestructive: true
      }
    );
    if (!confirmed) return;

    setIsSavingBooking(true);
    setBookingActionError('');
    try {
      const baseBooking = editingBookingOriginal || editingBooking;
      await api.updateBooking({ ...baseBooking, status: 'cancelled' });
      closeEditBooking();
      await refreshCompanyData();
      showAlertModal('Appointment cancelled.');
    } catch (e: unknown) {
      setBookingActionError(normalizeErrorMessage(e, 'Failed to cancel appointment.'));
    } finally {
      setIsSavingBooking(false);
    }
  }, [editingBooking, editingBookingOriginal, closeEditBooking, refreshCompanyData]);

  const handleViewCertificate = useCallback(
    (app: Appointment) => {
      if (!app.certificateId || !systemConfig) return;
      const cert = certificatesById.get(app.certificateId);
      if (!cert) return;

      const html = buildCertificateHtml({
        template: systemConfig.templates.certificateTemplate.body,
        systemConfig,
        appointment: app,
        certificate: cert,
        clientName: resolveCertificateClientName(app, company.name)
      });

      openCertificatePrintWindow(
        html,
        buildCertificateFileName({
          appointment: app,
          certificate: cert
        })
      );
    },
    [certificatesById, company.name, systemConfig]
  );

  useEffect(() => {
    if (!editingBooking?.venueId || !editingBooking?.date || !editingBooking?.serviceId) {
      setEditingAvailabilitySlots([]);
      setHasEditingAvailabilityData(false);
      setIsLoadingEditingAvailability(false);
      return;
    }

    const dateValue = normalizeDateOnly(editingBooking.date);
    if (!dateValue) {
      setEditingAvailabilitySlots([]);
      setHasEditingAvailabilityData(false);
      setIsLoadingEditingAvailability(false);
      return;
    }

    let isCurrent = true;
    setIsLoadingEditingAvailability(true);
    setHasEditingAvailabilityData(false);

    api
      .getBookingAvailability({
        venueId: editingBooking.venueId,
        date: dateValue,
        serviceId: editingBooking.serviceId,
        addOnIds: editingBooking.addOnIds || [],
        excludeAppointmentId: editingBooking.id
      })
      .then((result) => {
        if (!isCurrent) return;
        const slots = filterPastAvailabilitySlots(dateValue, result?.slots || []);
        setEditingAvailabilitySlots(slots);
        setHasEditingAvailabilityData(true);
        setEditingBooking((prev) => {
          if (!prev?.time) return prev;
          const stillAvailable = slots.some((slot) => slot.time === prev.time && slot.available);
          return stillAvailable ? prev : { ...prev, time: '' };
        });
      })
      .catch(() => {
        if (!isCurrent) return;
        setEditingAvailabilitySlots([]);
        setHasEditingAvailabilityData(false);
      })
      .finally(() => {
        if (isCurrent) setIsLoadingEditingAvailability(false);
      });

    return () => {
      isCurrent = false;
    };
  }, [
    editingBooking?.id,
    editingBooking?.date,
    editingBooking?.addOnIds,
    editingBooking?.serviceId,
    editingBooking?.venueId
  ]);

  const editingTimeOptions = useMemo(() => {
    if (!editingBooking) return [];
    const dateValue = normalizeDateOnly(editingBooking.date);
    if (!dateValue) return [];

    const [year, month, day] = dateValue.split('-').map(Number);
    const date = new Date(year, (month || 1) - 1, day || 1);
    if (Number.isNaN(date.getTime())) return [];

    const venue = venuesById.get(editingBooking.venueId);
    const generated = getAvailableTimeSlotsForDate(date, venue?.schedule, {
      requiredDurationMinutes: getBookingDurationMinutes(editingBooking)
    });

    const available = hasEditingAvailabilityData
      ? editingAvailabilitySlots.filter((slot) => slot.available).map((slot) => slot.time)
      : generated;

    if (!editingBooking.time || available.includes(editingBooking.time)) {
      return available;
    }

    return [editingBooking.time, ...available];
  }, [
    editingBooking,
    venuesById,
    getBookingDurationMinutes,
    hasEditingAvailabilityData,
    editingAvailabilitySlots
  ]);

  return {
    editingBooking,
    setEditingBooking,
    isLoadingEditingAvailability,
    editingTimeOptions,
    bookingActionError,
    isSavingBooking,
    openEditBooking,
    closeEditBooking,
    handleSaveBooking,
    handleCancelBooking,
    handleViewCertificate
  };
};
